/* App Engine cfg service — talks to /cm/app-engine/* (bex-server proxy →
 * BeXO /api/v1/bexo/config). Convention mirror of
 * dataHubV2/api/v2.service.ts: axios bex client + org_id/app_id params. */
import {
    bexHttpClient
} from 'shared/api/clients/bex.httpClient';
import type {
    AppListEntry,
    BootstrapRequest,
    ConnectSourceRequest,
    DefListItem,
    DefType,
    IdentityDoc,
    InternalConnection,
    Outcome,
    RunRecord,
    Tenancy,
    TryWorkflowResult,
    ValidateResult,
    WorkflowRunFilters,
    WorkflowRunPage,
    WorkflowRunRecord,
    WorkflowRunStats
} from './types';

const BASE = '/app-engine';

/** Engines may return a bare array or an { items } wrapper — accept both. */
function asList<T>(data: any, key = 'items'): T[] {
    if (Array.isArray(data)) return data as T[];
    if (data && Array.isArray(data[key])) return data[key] as T[];
    return [];
}

class AppEngineService {
    private q(t: Tenancy, extra: Record<string, any> = {}) {
        return {
            params: {
                org_id: t.org_id,
                app_id: t.app_id,
                ...extra
            }
        };
    }

    // ── Apps (org-level) ─────────────────────────────────────────────────
    /** NOTE: not in the locked cfg contract (cfg is app-scoped). The UI
     * needs an org-level list — backend must add GET /apps?org_id=. */
    async listApps(orgId: string): Promise<AppListEntry[]> {
        const r = await bexHttpClient.get(`${BASE}/apps`, {
            params: {
                org_id: orgId
            }
        });
        return asList<AppListEntry>(r.data, 'apps');
    }

    /** Org-only: app_id is ENGINE-MINTED (app_<12hex>) — the response's
     * app_id is the tenancy key; a body app_id would be ignored upstream. */
    async bootstrap(orgId: string, body: BootstrapRequest): Promise<any> {
        const r = await bexHttpClient.post(`${BASE}/bootstrap`, body, {
            params: {
                org_id: orgId
            }
        });
        return r.data;
    }

    /** ONE-PASTE app bundle import — create OR modify a whole app from a
     * single bundle { app, sources[], workflows[], journeys[], roles[],
     * creator? }. BeXO auto-detects create (mint app_id + seed birth-kit, then
     * upsert) vs modify (app.id resolves → upsert at supplied ids). Org-only
     * (app_id is engine-minted on create). Resolves to
     * { mode, app_id, saved[], errors{}, warnings{} }. A 207 (partial) and a
     * 400 (nothing saved) both carry that report, so accept < 500 and return
     * the body — the dialog renders saved vs errors uniformly. */
    async importApp(orgId: string, bundle: any): Promise<any> {
        const r = await bexHttpClient.post(`${BASE}/import`, bundle, {
            params: {
                org_id: orgId
            },
            validateStatus: (s) => s < 500
        });
        return r.data;
    }

    /** Delete an app entirely — BeXO drops its per-app Mongo DB + all its
     * Redis def keys + the org apps-index entry. Org-only (app_id is the path
     * target). The shared demo app (`common`) is refused upstream (400). */
    async deleteApp(orgId: string, appId: string): Promise<any> {
        const r = await bexHttpClient.delete(
            `${BASE}/apps/${encodeURIComponent(appId)}`, {
                params: {
                    org_id: orgId
                }
            });
        return r.data;
    }

    /** Duplicate an app — BeXO deep-copies the whole CONFIG (app doc +
     * journeys + workflows + sources) with FRESH remapped ids, plus the role
     * grants (journeys remapped) and users; business data is NOT copied. The
     * copy is named "<original name> - duplicate N" (auto-incremented per org)
     * unless an override name is supplied. Org-only (app_id is the path
     * target); the org apps-index updates on save so the copy auto-lists. */
    async duplicateApp(orgId: string, appId: string,
        body: { name?: string; description?: string } = {}): Promise<any> {
        const r = await bexHttpClient.post(
            `${BASE}/apps/${encodeURIComponent(appId)}/duplicate`, body, {
                params: {
                    org_id: orgId
                }
            });
        return r.data;
    }

    /** Rename an app — BeXO loads the app doc, sets its display name, saves,
     * and refreshes the org apps-index / any name cache so the list reflects
     * the new name immediately. Org-only (app_id is the path target); returns
     * the updated app summary. Mirrors /duplicate. */
    async renameApp(orgId: string, appId: string, name: string): Promise<any> {
        const r = await bexHttpClient.post(
            `${BASE}/apps/${encodeURIComponent(appId)}/rename`, {
                name
            }, {
                params: {
                    org_id: orgId
                }
            });
        return r.data;
    }

    /** Whole-app EXPORT — GET /apps/{appId}/export returns the app as one
     * bundle { app, sources[], workflows[], journeys[], roles[] } (app-local
     * defs, all ids preserved). Org-scoped (app_id is the path target). The
     * paired inverse of importApp: export → edit → importApp round-trips. */
    async exportApp(orgId: string, appId: string): Promise<any> {
        const r = await bexHttpClient.get(
            `${BASE}/apps/${encodeURIComponent(appId)}/export`, {
                params: {
                    org_id: orgId
                }
            });
        return r.data;
    }

    /** Renderer catalog (the closed component set) — GET /catalog,
     * org-only tenancy like /apps and /guide. The endpoint lands with the
     * engine lane; callers MUST fall back to the bundled summary on
     * failure (see screens/learn/useCatalog). */
    async getCatalog(orgId: string): Promise<any> {
        const r = await bexHttpClient.get(`${BASE}/catalog`, {
            params: {
                org_id: orgId
            }
        });
        return r.data;
    }

    // ── Definitions ──────────────────────────────────────────────────────
    async listDefs(t: Tenancy, type: DefType): Promise<DefListItem[]> {
        const r = await bexHttpClient.get(`${BASE}/defs/${type}`, this.q(t));
        return asList<DefListItem>(r.data);
    }

    async getDef(t: Tenancy, type: DefType, id: string): Promise<any> {
        const r = await bexHttpClient.get(
            `${BASE}/defs/${type}/${encodeURIComponent(id)}`, this.q(t));
        return r.data;
    }

    /** CREATE with an engine-minted id (wf_/jrn_/src_<12hex>): body = the doc
     * WITHOUT an id (a supplied id is stripped+ignored upstream); returns the
     * full saved doc { id, ... } — open the editor at that minted id. */
    async createDef(t: Tenancy, type: DefType, doc: any): Promise<any> {
        const r = await bexHttpClient.post(`${BASE}/defs/${type}`, doc, this.q(t));
        return r.data;
    }

    /** UPDATE-ONLY validate-then-save: 404 on an unknown id (create via
     * createDef). A 400 carries { errors: [] } — callers catch and
     * surface those verbatim. */
    async putDef(t: Tenancy, type: DefType, id: string, doc: any): Promise<any> {
        const r = await bexHttpClient.put(
            `${BASE}/defs/${type}/${encodeURIComponent(id)}`, doc, this.q(t));
        return r.data;
    }

    /** DELETE INTEGRITY: without force a referenced doc answers 409
     * {error:"referenced", referenced_by:[...]}; force=true deletes,
     * auto-cleans grants and reports left_dangling definition referrers. */
    async deleteDef(t: Tenancy, type: DefType, id: string,
        force = false): Promise<any> {
        const r = await bexHttpClient.delete(
            `${BASE}/defs/${type}/${encodeURIComponent(id)}`,
            this.q(t, force ? {
                force: 1
            } : {}));
        return r.data;
    }

    async validate(t: Tenancy, type: DefType, doc: any): Promise<ValidateResult> {
        const r = await bexHttpClient.post(`${BASE}/validate/${type}`, doc, this.q(t));
        const d = r.data || {};
        return {
            valid: !!d.valid,
            errors: Array.isArray(d.errors) ? d.errors : [],
            warnings: Array.isArray(d.warnings) ? d.warnings : []
        };
    }

    // ── Sources ──────────────────────────────────────────────────────────
    /** A-0017: the org's INTERNAL, platform-provisioned connections — named
     * entries the ENGINE resolves server-side, so the author never sees (and
     * is never handed) the org's secret. The response carries NO credential
     * of any kind: { items: [{ id, name, source_type, description,
     * provisioned, requires[] }] }, plus unprovisioned_reason when
     * provisioned is false.
     *
     * Goes through bex-server like every other call (/cm/app-engine/... via
     * the /api → /cm rewrite) — the platform NEVER calls BeXO directly. */
    async listInternalConnections(t: Tenancy): Promise<InternalConnection[]> {
        const r = await bexHttpClient.get(`${BASE}/internal-connections`,
            this.q(t));
        return asList<InternalConnection>(r.data);
    }

    /** ASYNC / non-blocking connect: enqueues a background ingest job and
     * returns { job_id, status:"running" } immediately — the live SCAN /
     * tools-list / spec-fetch / DB-introspection runs OFF the request thread
     * server-side and the drafted source doc is saved when ready. Poll
     * getConnectJob(jobId) until status is "done" (with source_id +
     * capabilities) or "failed" (with errors). */
    async connectSource(t: Tenancy, body: ConnectSourceRequest): Promise<any> {
        const r = await bexHttpClient.post(`${BASE}/sources/connect`, body, this.q(t));
        return r.data;   // { job_id, status:"running" }
    }

    /** Poll an async connect job:
     * { job_id, status:"running"|"done"|"failed", source_id?, capabilities?, errors? }. */
    async getConnectJob(t: Tenancy, jobId: string): Promise<any> {
        const r = await bexHttpClient.get(
            `${BASE}/sources/connect/jobs/${encodeURIComponent(jobId)}`, this.q(t));
        return r.data;
    }

    /** A-0003: ensure the RediSearch index behind a redis source's ft_search
     * capability (explicit provisioning; idempotent — existing = success).
     * → { ok, index, prefix, created|existed, fields[] }. */
    async ensureSearchIndex(t: Tenancy, sourceId: string,
        capabilityId?: string): Promise<any> {
        const r = await bexHttpClient.post(
            `${BASE}/sources/${encodeURIComponent(sourceId)}/search-index`,
            capabilityId ? { capability: capabilityId } : {}, this.q(t));
        return r.data;
    }

    /** A-0004: ensure the bucket CORS on a cos source so the browser can
     * upload/preview files directly against COS (explicit provisioning;
     * idempotent — existing = success).
     * → { ok, bucket, applied, origins }. */
    async ensureBucketCors(t: Tenancy, sourceId: string,
        capabilityId?: string): Promise<any> {
        const r = await bexHttpClient.post(
            `${BASE}/sources/${encodeURIComponent(sourceId)}/cos-cors`,
            capabilityId ? { capability: capabilityId } : {}, this.q(t));
        return r.data;
    }
    // reingestSource REMOVED (sources rewire): re-ingest is gone; refresh a
    // source by connecting it again (origin-aware merge preserves edits).

    // ── Execute (workflow run) ───────────────────────────────────────────
    // The PLATFORM must never touch the EXECUTE plane — that lane belongs to
    // the running app. Build/admin runs (the Studio Try drawer, identity
    // admin) go through the CONFIG-plane execute endpoint:
    //   POST /cm/app-engine/config/workflows/{id}/execute
    // (bex-server relays to BeXO's config-base
    //  /api/v1/bexo/config/workflows/{id}/execute, same handler body as the
    //  execute-plane run but carrying the cfg tenancy + identity_store wiring
    //  so app-local mongo resolves). The execute plane stays untouched for
    //  the live app's surface actions.
    async executeWorkflowConfig(t: Tenancy, workflowId: string, input: any):
        Promise<TryWorkflowResult> {
        const r = await bexHttpClient.post(
            `${BASE}/config/workflows/${encodeURIComponent(workflowId)}/execute`, {
                input
            }, this.q(t));
        return r.data as TryWorkflowResult;
    }

    /** Studio Try alias — same config-plane endpoint. Kept as a distinct
     * name so the Try surface reads as "try" while sharing the one lane. */
    async executeWorkflow(t: Tenancy, workflowId: string, input: any):
        Promise<TryWorkflowResult> {
        return this.executeWorkflowConfig(t, workflowId, input);
    }

    // ── Identity reads (users / roles) over the config-plane workflows ────
    // The mongo-backed src_identity is read through wf_list_users /
    // wf_list_roles (replacing the removed GET /identity/{collection}). Both
    // return the projected doc rows ({id,email,name,roles} / {id,name,
    // journeys}); password is projected out by the workflow.
    private listFromWorkflow(out: any): IdentityDoc[] {
        if (Array.isArray(out)) return out as IdentityDoc[];
        return asList<IdentityDoc>(out);
    }

    async listUsers(t: Tenancy): Promise<IdentityDoc[]> {
        const r = await this.executeWorkflowConfig(t, 'wf_list_users', {});
        return this.listFromWorkflow(r?.output);
    }

    async listRoles(t: Tenancy): Promise<IdentityDoc[]> {
        const r = await this.executeWorkflowConfig(t, 'wf_list_roles', {});
        return this.listFromWorkflow(r?.output);
    }

    // ── Runs ─────────────────────────────────────────────────────────────
    async listRuns(t: Tenancy, filters: {
        status?: string;
        journey?: string;
    } = {}): Promise<RunRecord[]> {
        const extra: Record<string, any> = {};
        if (filters.status) extra.status = filters.status;
        if (filters.journey) extra.journey = filters.journey;
        const r = await bexHttpClient.get(`${BASE}/runs`, this.q(t, extra));
        // Live cfg API wraps run summaries as { items: [...] } (verified
        // against BeXO /api/v1/bexo/config/runs — NOT { runs: [...] }).
        return asList<RunRecord>(r.data);
    }

    async getRun(t: Tenancy, runId: string): Promise<RunRecord> {
        const r = await bexHttpClient.get(
            `${BASE}/runs/${encodeURIComponent(runId)}`, this.q(t));
        return r.data as RunRecord;
    }

    // ── Workflow runs (A-0017 T-50: the run LEDGER in the org's Mongo) ───
    // Every workflow run — from the app at runtime, the Try drawer, the
    // Pilotage MCP or a job — with full input/output and every step's own
    // input/output/status/timing. List rows omit input/output/steps.
    async listWorkflowRuns(t: Tenancy, filters: WorkflowRunFilters = {}):
        Promise<WorkflowRunPage> {
        const extra: Record<string, any> = {};
        for (const [k, v] of Object.entries(filters)) {
            if (v !== undefined && v !== null && v !== '') extra[k] = v;
        }
        const r = await bexHttpClient.get(`${BASE}/workflow-runs`, this.q(t, extra));
        const d = r.data || {};
        return {
            items: asList<WorkflowRunRecord>(d),
            total: Number(d.total ?? 0),
            limit: Number(d.limit ?? 50),
            skip: Number(d.skip ?? 0),
        };
    }

    async getWorkflowRun(t: Tenancy, runId: string): Promise<WorkflowRunRecord> {
        const r = await bexHttpClient.get(
            `${BASE}/workflow-runs/${encodeURIComponent(runId)}`, this.q(t));
        return r.data as WorkflowRunRecord;
    }

    async workflowRunStats(t: Tenancy, filters: WorkflowRunFilters = {}):
        Promise<WorkflowRunStats> {
        const extra: Record<string, any> = {};
        for (const [k, v] of Object.entries(filters)) {
            if (v !== undefined && v !== null && v !== '') extra[k] = v;
        }
        const r = await bexHttpClient.get(`${BASE}/workflow-runs/stats`, this.q(t, extra));
        return r.data as WorkflowRunStats;
    }
}

export const appEngineService = new AppEngineService();

/** Extract the validator errors[] from a failed save/validate axios error.
 * The live engine raises HTTPException(detail={errors: []}), so the array
 * may sit at data.errors OR data.detail.errors — accept both. */
export function extractValidatorErrors(err: any): string[] | null {
    const data = err?.response?.data;
    if (data && Array.isArray(data.errors)) return data.errors;
    if (data?.detail && Array.isArray(data.detail.errors)) return data.detail.errors;
    return null;
}

/** Advisory warnings[] off a PUT/POST /defs success response (or any
 * payload that carries them) — defensive: missing/odd shapes → []. */
export function extractWarnings(data: any): string[] {
    if (data && Array.isArray(data.warnings)) {
        return data.warnings.map((w: any) => String(w));
    }
    return [];
}

/** referenced_by[] off a DELETE-integrity 409 ({error:"referenced"}).
 * Returns null when the error is anything else. */
export function extractReferencedBy(err: any): any[] | null {
    if (err?.response?.status !== 409) return null;
    const data = err.response.data;
    const refs = Array.isArray(data?.referenced_by)
        ? data.referenced_by
        : data?.detail?.referenced_by;
    return Array.isArray(refs) ? refs : null;
}

/** Human message for non-validator failures. */
export function describeError(err: any): string {
    const data = err?.response?.data;
    if (typeof data?.detail === 'string') return data.detail;
    if (typeof data?.message === 'string') return data.message;
    if (err?.message) return String(err.message);
    return 'Request failed';
}

/** A closed-outcome failure off a config-plane workflow run. Identity guard
 * workflows surface their blocks as { outcome: { ok:false, status, ... } }
 * (validation_error · conflict · not_found) rather than HTTP errors, so the
 * caller inspects the OUTCOME, not the response status. */
export interface WorkflowFailure {
    status: string;
    message: string;
    /** conflict (role-referenced) carries the holders so the UI can offer
     * force-clean — the count and, when returned, the names/ids. */
    referenced?: any[];
    count?: number;
    /** the raw outcome for any extra fields a surface wants to read. */
    outcome: Outcome;
}

/** Result of mapping a workflow run: ok carries the OUTPUT; otherwise
 * failure carries the closed WorkflowFailure. A single flat shape (not a
 * discriminated union) — this project compiles with strictNullChecks off,
 * where union-discriminant narrowing is unreliable, so the consumer just
 * reads .ok then .output / .failure. */
export interface WorkflowReadResult {
    ok: boolean;
    output?: any;
    failure?: WorkflowFailure;
}

/** Map a workflow result to either its OUTPUT (success) or a closed
 * WorkflowFailure (any non-ok outcome). The guard workflows put the human
 * reason on outcome.reason|message|error and a referenced list on
 * outcome.referenced|users|holders with a count on outcome.count. */
export function readWorkflow(res: TryWorkflowResult | null | undefined):
    WorkflowReadResult {
    const outcome: Outcome = (res?.outcome as Outcome) || {
        ok: false,
        status: 'unknown'
    };
    if (outcome.ok) {
        const out: any = res?.output;
        // The COMPOSITE guard workflows (wf_delete_user / wf_delete_role)
        // return a SUCCESSFUL run whose OUTPUT names a block:
        // {deleted:false, status, reason, referenced_count}. Treat that as a
        // failure so the UI surfaces the guard (last-admin / structural /
        // role-referenced) rather than reporting a phantom success.
        if (out && typeof out === 'object' && out.deleted === false) {
            return {
                ok: false,
                failure: {
                    status: String(out.status || 'error'),
                    message: String(out.reason || `Rejected (${out.status})`),
                    count: typeof out.referenced_count === 'number'
                        ? out.referenced_count
                        : undefined,
                    outcome
                }
            };
        }
        return {
            ok: true,
            output: out
        };
    }
    const referenced = Array.isArray((outcome as any).referenced)
        ? (outcome as any).referenced
        : Array.isArray((outcome as any).users)
            ? (outcome as any).users
            : Array.isArray((outcome as any).holders)
                ? (outcome as any).holders
                : undefined;
    const count = typeof (outcome as any).count === 'number'
        ? (outcome as any).count
        : referenced
            ? referenced.length
            : undefined;
    const message = String(
        (outcome as any).reason
        || (outcome as any).message
        || (outcome as any).error
        || `Rejected (${outcome.status})`);
    return {
        ok: false,
        failure: {
            status: String(outcome.status || 'error'),
            message,
            referenced,
            count,
            outcome
        }
    };
}
